<template>
	<div v-if="manualValidities.length > 0" class="manual-contingents-warning">
		<div class="warning-header">
			<div class="warning-content">
				<v-icon color="#ffa726">mdi-alert</v-icon>
				<span class="warning-text">
					{{ $t('text.manualContingentsWarningText', { count: manualValidities.length }) }}
				</span>
			</div>
			<v-btn
				size="small"
				icon
				variant="text"
				@click="$emit('hide')"
				v-if="closable"
				class="hide-warning-button"
			>
				<v-icon size="16" color="#f57c00">mdi-close</v-icon>
			</v-btn>
		</div>
		<div class="warning-description">
			{{ $t('text.manualContingentsWarningDescription', { count: manualValidities.length }) }}
		</div>
		
		<!-- Manual Contingents List -->
		<div class="manual-contingents-section">
			<div class="manual-contingents-header">
				<h4>{{ $t('text.manualContingentsTitle') }}</h4>
				<p class="manual-contingents-description">
					{{ $t('text.manualContingentsDescription') }}
				</p>
				<a href="#" @click.prevent="toggleList" class="show-manual-list-link">
					{{ isInternalListExpanded ? $t('text.hide') : $t('text.show') }} {{ $t('text.manualContingentsList') }}
				</a>
			</div>

			<div v-if="isInternalListExpanded" class="manual-contingents-list">
				<div class="manual-contingents-table-header">
					<div class="time-column-header">{{ $t('text.time') }}</div>
					<div class="contingent-column-header">{{ $t('text.contingent') }}</div>
				</div>
				<div v-for="validity in manualValidities" 
					:key="`${validity.validityId}-${validity.displayDate}`"
					class="manual-contingent-row"
				>
					<div class="time-column">
						<div class="date-text clickable-date" @click="$emit('navigate-to-date', validity.displayDate)" :title="$t('text.jumpToDateInCalendar')">
							{{ validity.formattedDate }}
						</div>
						<div class="time-text">{{ validity.formattedTime }}</div>
					</div>
					<div class="contingent-column">
						<span class="contingent-value">{{ validity.total }}</span>
						<v-btn 
							size="small" 
							icon 
							variant="text" 
							@click="deleteManualContingent(validity)"
							class="delete-button"
						>
							<v-icon size="16" color="#ff4444">mdi-delete</v-icon>
						</v-btn>
					</div>
				</div>
			</div>
		</div>

		<!-- Delete Manual Contingent Dialog -->
		<Dialog ref="deleteManualContingentDialog"
			:confirmLabel="$t('text.delete')"
			:cancelLabel="$t('text.cancel')"
			:confirm-handler="onDeleteManualContingentConfirmed"
			:cancel-handler="onDeleteManualContingentCancelled"
			:showClose="false"
			:title="$t('text.deleteManualContingentTitle')"
			height="360px"
			width="540px"
		>
			<template #content>
				<v-row justify="center" align="center" style="padding: 10px">
					<v-col class="warning-icon-col">
						<img class="warningDialogImage" src="@/assets/icons/icon-warning.svg" />
					</v-col>
					<v-col class="text-col">
						<span class="dialogText">{{ deleteConfirmationMessage }}</span>
					</v-col>
				</v-row>
			</template>
		</Dialog>
	</div>
</template>

<script>
import Dialog from '@/components/common/Dialog.vue'
import Toast from '@/mixins/Toast.vue'

export default {
	name: 'ManualContingentsWarning',
	components: { Dialog },
	mixins: [ Toast ],
	props: {
		manualValidities: {
			type: Array,
			required: true
		},
		expandedKey: {
			type: String,
			required: true
		},
		spaceId: {
			type: [String, Number],
			required: true
		},
		closable: {
			type: Boolean,
			default: true
		}
	},
	emits: ['hide', 'navigate-to-date', 'validity-deleted'],
	data: () => ({
		isInternalListExpanded: false,
		validityToDelete: null,
		deleteConfirmationMessage: ''
	}),
	methods: {
		toggleList() {
			this.isInternalListExpanded = !this.isInternalListExpanded
		},
		deleteManualContingent(validity) {
			// Store the validity to delete and show confirmation dialog
			this.validityToDelete = validity
			this.deleteConfirmationMessage = this.$t('text.deleteManualContingentConfirmation', { 
				date: validity.formattedDate, 
				time: validity.formattedTime 
			})
			this.$refs.deleteManualContingentDialog.show = true
		},
		async onDeleteManualContingentConfirmed() {
			try {
				const validity = this.validityToDelete
				const clientId = this.$store.state.selectedClient.fields.clientId?.de
				const spaceId = this.spaceId
				
				if (!spaceId) {
					throw new Error('SpaceId not found')
				}
				
				await this.$httpDelete(`/manual-validities/${clientId}/${spaceId}/${validity.validityId}/${validity.contingentAssignmentId}`)
				
				// Show success toast
				this.showSuccessToast('manualContingentDeletedSuccessfully')
				
				// Emit event to parent to handle the deletion (refresh data, etc.)
				this.$emit('validity-deleted', validity)
				
			} catch (error) {
				console.error('Error deleting manual contingent:', error)
				this.showErrorToast('manualContingentDeleteFailed')
			} finally {
				this.$refs.deleteManualContingentDialog.show = false
				this.validityToDelete = null
			}
		},
		onDeleteManualContingentCancelled() {
			this.$refs.deleteManualContingentDialog.show = false
			this.validityToDelete = null
		}
	}
}
</script>

<style scoped>
	.manual-contingents-warning {
		background-color: #fefcf8;
		border: none;
		border-radius: 8px;
		padding: 16px;
		margin-top: 8px;
		margin-bottom: 8px;
		box-shadow: 0 1px 3px rgba(255, 167, 38, 0.1);
	}

	.warning-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;
	}

	.warning-content {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.warning-text {
		font-weight: 600;
		color: #f57c00;
	}

	.warning-description {
		font-size: 14px;
		color: #f57c00;
		line-height: 1.4;
	}

	.hide-warning-button {
		opacity: 0.7;
		transition: opacity 0.2s;
	}

	.hide-warning-button:hover {
		opacity: 1;
	}

	.manual-contingents-section {
		background-color: #ffffff;
		border: none;
		border-radius: 6px;
		padding: 16px;
		margin-top: 12px;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	}

	.manual-contingents-header h4 {
		margin: 0 0 8px 0;
		font-size: 16px;
		font-weight: 600;
		color: #333;
	}

	.manual-contingents-description {
		font-size: 14px;
		color: #666;
		margin-bottom: 12px;
		line-height: 1.4;
	}

	.show-manual-list-link {
		color: #1976d2;
		text-decoration: none;
		font-size: 14px;
	}

	.show-manual-list-link:hover {
		text-decoration: underline;
	}

	.manual-contingents-list {
		margin-top: 12px;
		border: 1px solid #f0f0f0;
		border-radius: 4px;
		overflow: hidden;
	}

	.manual-contingents-table-header {
		display: flex;
		background-color: #fafafa;
		border-bottom: 1px solid #f0f0f0;
		font-weight: 600;
		font-size: 14px;
		color: #333;
	}

	.time-column-header,
	.contingent-column-header {
		padding: 12px 16px;
		flex: 1;
	}

	.manual-contingent-row {
		display: flex;
		border-bottom: 1px solid #f8f8f8;
		background-color: white;
	}

	.manual-contingent-row:last-child {
		border-bottom: none;
	}

	.manual-contingent-row:hover {
		background-color: #f9f9f9;
	}

	.time-column,
	.contingent-column {
		padding: 12px 16px;
		flex: 1;
	}

	.date-text {
		font-size: 14px;
		color: #333;
		font-weight: 500;
	}

	.clickable-date {
		cursor: pointer;
		color: #1976d2;
		transition: color 0.2s;
	}

	.clickable-date:hover {
		color: #1565c0;
		text-decoration: underline;
	}

	.time-text {
		font-size: 12px;
		color: #666;
		margin-top: 2px;
	}

	.contingent-column {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.contingent-value {
		font-size: 14px;
		font-weight: 500;
		color: #333;
	}

	.delete-button {
		opacity: 0.7;
		transition: opacity 0.2s;
	}

	.delete-button:hover {
		opacity: 1;
	}

	.warning-icon-col {
		flex: 1;
		text-align: center;
	}

	.text-col {
		flex: 2;
		text-align: center;
		
		width: 268px;
		height: 110px;
		margin: 8px 0 0;
		font-size: 17px;
		font-weight: normal;
		font-stretch:normal;
		font-style: normal;
		line-height: 1.29;
		letter-spacing: -0.42px;
		color: #9da0a5;

		text-align: left;
	}

	.dialogText {
		color: #9da0a5;
		font-size: 17px;
		font-weight: normal;
		line-height: 1.29;
		letter-spacing: -0.42px;
	}

	.warningDialogImage {
		max-width: 60px;
		height: auto;
	}
</style>
